import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCsrfToken } from '../../utils/csrf';
import { CashflowHistoryItem, CashflowTrendData } from '../models/ModelCashflow';

export const useCashflowDash = () => {
  const navigate = useNavigate();
  const [cashflowHistory, setCashflowHistory] = useState<CashflowHistoryItem[]>([]);
  const [trendData, setTrendData] = useState<CashflowTrendData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCashflowData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const csrfToken = await getCsrfToken();
      const response = await fetch('/api/cashflow/', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
      });

      if (response.redirected) {
        window.location.href = '/login';
        return;
      }

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 400) {
          // Handle specific error cases
          if (result.error === 'No income data found') {
            setError('income');
            navigate('/income');
            return;
          }
          if (result.error === 'No expense data found') {
            setError('expense');
            navigate('/expenses');
            return;
          }
        }
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }
      
      if (!result.success || !result.has_cashflow_data) {
        navigate('/cashflow');
        return;
      }
      
      const sortedHistory = [...result.data.cashflow_history].sort(
        (a: CashflowHistoryItem, b: CashflowHistoryItem) => 
        new Date(a.month).getTime() - new Date(b.month).getTime()
      );
      
      setCashflowHistory(sortedHistory);
      setTrendData(result.data.trend_data);
    } catch (error) {
      console.error('Error fetching cashflow data:', error);
      if (error instanceof Error && error.message === 'Failed to fetch CSRF token') {
        window.location.href = '/login';
        return;
      }
      setError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const clearCashflowData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const csrfToken = await getCsrfToken();
      const response = await fetch('/api/cashflow/clear/', {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
      });

      if (response.redirected) {
        window.location.href = '/login';
        return;
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }

      // Refresh the data after clearing
      await fetchCashflowData();
    } catch (error) {
      console.error('Error clearing cashflow data:', error);
      setError(error instanceof Error ? error.message : 'An error occurred while clearing data');
    } finally {
      setIsLoading(false);
    }
  }, [fetchCashflowData]);

  const resetCashflowData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const csrfToken = await getCsrfToken();
      const response = await fetch('/api/cashflow/reset/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
      });

      if (response.redirected) {
        window.location.href = '/login';
        return;
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }

      // Refresh the data after resetting
      await fetchCashflowData();
    } catch (error) {
      console.error('Error resetting cashflow data:', error);
      setError(error instanceof Error ? error.message : 'An error occurred while resetting data');
    } finally {
      setIsLoading(false);
    }
  }, [fetchCashflowData]);

  return {
    cashflowHistory,
    trendData,
    fetchCashflowData,
    clearCashflowData,
    resetCashflowData,
    isLoading,
    error,
  };
};
